import React, { useRef, useEffect, useMemo, memo } from 'react';
import { View, Text, StyleSheet, type ScrollView } from 'react-native';
import moment from 'moment';
import { getLocale, formatNumber, layoutLtrStyle } from '../../../shared/i18n';
import { toAsciiDateKey } from '../../../shared/utils/apiParams';
import { Clickable } from '../../../components/common/Clickable';
import { appTheme, useThemeColors, type ThemeColors } from '../../../shared/theme';
import { HorizontalLtrScrollView } from '../../../components/ui/HorizontalLtrScrollView';

const ITEM_WIDTH = 50;
const ITEM_SPACING = 6;
const FULL_ITEM_WIDTH = ITEM_WIDTH + ITEM_SPACING;
const ROW_HEIGHT = 60;
const PILL_R = ITEM_WIDTH / 2;

function getDateItemTextColor(c: ThemeColors, is_selected: boolean, is_disabled: boolean, is_forced_disabled: boolean): string {
  if (is_forced_disabled) {
    return c.text;
  }
  if (is_disabled) {
    return c.textLight;
  }
  if (is_selected) {
    return c.onPrimary;
  }
  return c.text;
}

function makeDatePillStyles(c: ThemeColors) {
  return StyleSheet.create({
    itemWrap: {
      width: ITEM_WIDTH,
      height: ITEM_WIDTH,
      marginHorizontal: ITEM_SPACING,
      borderRadius: PILL_R,
      overflow: 'hidden',
    },
    pillInner: {
      position: 'relative',
      width: ITEM_WIDTH - 2,
      height: ITEM_WIDTH - 2,
      minWidth: ITEM_WIDTH - 2,
      padding: 0,
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: PILL_R - 1,
      flexDirection: 'column',
      backgroundColor: c.surface,
      borderColor: c.primaryLight,
      borderWidth: 1,
      overflow: 'hidden',
    },
    pill: {
      width: ITEM_WIDTH,
      height: ITEM_WIDTH,
      minWidth: ITEM_WIDTH,
      padding: 0,
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'column',
      borderRadius: PILL_R,
      ...appTheme.shadows.xl,
    },
    pillSelected: {
      backgroundColor: c.primary,
      borderColor: c.primaryLight,
      borderWidth: 1,
    },
    pillForced: {
      backgroundColor: c.secondaryUltra,
      borderColor: c.error,
      borderWidth: 1,
    },
    dayName: {
      fontSize: 8,
      fontWeight: '600',
      marginBottom: 2,
      textTransform: 'uppercase',
      textAlign: 'center',
    },
    dayNum: {
      fontSize: 16,
      fontWeight: '600',
      textAlign: 'center',
    },
    dayNumSelected: { fontSize: 18, fontWeight: '800' },
    activeIndicator: {
      position: 'absolute',
      bottom: -ITEM_WIDTH / 3,
      width: ITEM_WIDTH / 2,
      height: ITEM_WIDTH / 2,
      borderTopLeftRadius: ITEM_WIDTH / 2,
      borderTopRightRadius: ITEM_WIDTH / 2,
      borderBottomLeftRadius: 0,
      borderBottomRightRadius: 0,
      backgroundColor: 'transparent',
    },
    activeIndicatorOn: {
      backgroundColor: c.secondary,
    },
  });
}

type DatePillStyles = ReturnType<typeof makeDatePillStyles>;

interface MenuFlowDateItemProps {
  date: string;
  dayName: string;
  dayNumber: string;
  isSelected: boolean;
  isDisabled: boolean;
  isForcedDisabled: boolean;
  onPress: (date: string) => void;
  c: ThemeColors;
  pillStyles: DatePillStyles;
}

const MenuFlowDateItem = memo(({ date, dayName, dayNumber, isSelected, isDisabled, isForcedDisabled, onPress, c, pillStyles: s }: MenuFlowDateItemProps) => {
  const is_sel = isSelected && !isForcedDisabled;
  const text_color = getDateItemTextColor(c, isSelected, isDisabled, isForcedDisabled);
  const pill_style = [s.pillInner, is_sel && s.pillSelected, isForcedDisabled && s.pillForced];
  const indicator_style = [s.activeIndicator, is_sel && s.activeIndicatorOn];

  return (
    <View style={s.itemWrap}>
      <Clickable onPress={() => onPress(date)} disabled={isDisabled || isForcedDisabled} style={s.pill} accessibilityLabel={`${dayName} ${dayNumber}`} accessibilityRole="button">
        <View style={pill_style}>
          <Text allowFontScaling={false} numberOfLines={1} style={[s.dayName, { color: text_color }]}>
            {dayName}
          </Text>
          <Text allowFontScaling={false} numberOfLines={1} style={[s.dayNum, is_sel && s.dayNumSelected, { color: text_color }]}>
            {dayNumber}
          </Text>
          <View style={indicator_style} />
        </View>
      </Clickable>
    </View>
  );
});

interface ProcessedDateItem {
  date: string;
  dayName: string;
  dayNumber: string;
  isSelectable: boolean;
  isForced: boolean;
}

export interface MenuFlowDateSliderSet2Props {
  availableDates?: string[];
  selectableDates?: string[];
  selectedDate: string;
  onDateSelect: (date: string) => void;
  dayConfigMap?: Record<string, { forcedDisabled?: boolean }>;
  forcedDisabledDates?: string[];
}

function makeSliderChromeStyles() {
  return StyleSheet.create({
    root: {
      ...layoutLtrStyle,
      height: ROW_HEIGHT,
      justifyContent: 'center',
      overflow: 'hidden',
    },
    scrollContent: {
      ...layoutLtrStyle,
      flexDirection: 'row',
      paddingHorizontal: 16,
      alignItems: 'center',
      height: ROW_HEIGHT,
    },
  });
}

export const MenuFlowDateSliderSet2: React.FC<MenuFlowDateSliderSet2Props> = ({
  availableDates = [],
  selectableDates,
  selectedDate,
  onDateSelect,
  dayConfigMap = {},
  forcedDisabledDates = [],
}) => {
  const scroll_ref = useRef<ScrollView>(null);
  const c = useThemeColors() ?? appTheme.colors;
  const pill_styles = useMemo(() => makeDatePillStyles(c), [c]);
  const chrome = useMemo(() => makeSliderChromeStyles(), []);

  const locale = getLocale();
  const selectable_ascii = useMemo(
    () => new Set((selectableDates ?? []).map(d => toAsciiDateKey(d)).filter(Boolean)),
    [selectableDates],
  );
  const forced_ascii = useMemo(
    () => new Set(forcedDisabledDates.map(d => toAsciiDateKey(d)).filter(Boolean)),
    [forcedDisabledDates],
  );
  const selected_ascii = toAsciiDateKey(selectedDate);

  const processed_dates = useMemo(() => {
    const loc = locale === 'ar' ? 'ar' : 'en';
    try {
      moment.locale(loc);
    } catch {
      moment.locale('en');
    }
    return availableDates.map((d: string): ProcessedDateItem => {
      const date_ascii = toAsciiDateKey(d);
      const m = moment(date_ascii);
      const config = dayConfigMap[date_ascii] ?? dayConfigMap[d];
      return {
        date: date_ascii,
        dayName: m.format('ddd'),
        dayNumber: formatNumber(m.date()),
        isSelectable: selectableDates ? selectable_ascii.has(date_ascii) : !!config,
        isForced: !!config?.forcedDisabled || forced_ascii.has(date_ascii),
      };
    });
  }, [availableDates, selectableDates, selectable_ascii, dayConfigMap, forced_ascii, locale]);

  useEffect(() => {
    const idx = processed_dates.findIndex(row => row.date === selected_ascii);
    if (idx !== -1 && scroll_ref.current) {
      const x = Math.max(0, idx * FULL_ITEM_WIDTH - ITEM_WIDTH * 2);
      const timer = setTimeout(() => scroll_ref.current?.scrollTo({ x, animated: true }), 100);
      return () => clearTimeout(timer);
    }
  }, [selected_ascii, processed_dates]);

  return (
    <View style={chrome.root}>
      <HorizontalLtrScrollView
        ref={scroll_ref}
        horizontal
        directionalLockEnabled
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={chrome.scrollContent}
        snapToInterval={FULL_ITEM_WIDTH}
        decelerationRate="fast"
        removeClippedSubviews={false}
        scrollEventThrottle={32}
        disableIntervalMomentum>
        {processed_dates.map(item => (
          <MenuFlowDateItem
            key={item.date}
            date={item.date}
            dayName={item.dayName}
            dayNumber={item.dayNumber}
            isSelected={item.date === selected_ascii}
            isDisabled={!item.isSelectable}
            isForcedDisabled={item.isForced}
            onPress={onDateSelect}
            c={c}
            pillStyles={pill_styles}
          />
        ))}
      </HorizontalLtrScrollView>
    </View>
  );
};

export default memo(MenuFlowDateSliderSet2);
